import React, { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { Form } from '../../services/formService';
import { UserDataFieldDto } from '../../types/customUserDataFields';
import { useAutoSave, SaveStatus } from '../../hooks/useAutoSave';
import PDFPageComponent from './PDFPageComponent';

// Helper function to format a number with commas and decimal places
const formatNumber = (value: number | string, decimalPlaces: number = 2, useThousandsSeparator: boolean = true): string => {
  if (value === '' || value === null || value === undefined) return '';

  // Parse to number
  const num = typeof value === 'string' ? parseFloat(String(value).replace(/,/g, '')) : value;
  if (isNaN(num)) return String(value);

  // Format with decimal places
  const fixed = num.toFixed(decimalPlaces);

  if (!useThousandsSeparator) return fixed;

  // Add thousand separators
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

// Set up PDF.js worker using local file
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/pdf.worker.min.mjs';
} catch (error) {
  console.warn('Failed to set PDF.js worker source:', error);
}

// Check if PDF.js is working
const isPDFJSAvailable = () => {
  try {
    return typeof pdfjsLib.getDocument === 'function';
  } catch (error) {
    return false;
  }
};

const PDF_ZOOM_STORAGE_KEY = 'pdf_viewer_zoom_level';

export interface InteractivePDFViewerProps {
  pdfBytes: Uint8Array | null;
  loading: boolean;
  onDownloadAndSave?: (formData: { [key: string]: any }, saveOnly?: boolean, print?: boolean) => Promise<void> | void;
  onRetry?: () => void;
  form?: Form;
  userDataFields?: UserDataFieldDto[];
  initialFormData?: { [key: string]: any };
  onDirtyChange?: (isDirty: boolean) => void;
  // Auto-save props
  onAutoSave?: (formData: { [key: string]: any }) => Promise<void>;
  autoSaveEnabled?: boolean;
  autoSaveDebounceMs?: number;
}

const InteractivePDFViewer: React.FC<InteractivePDFViewerProps> = ({
  pdfBytes,
  loading,
  onDownloadAndSave,
  onRetry,
  form,
  userDataFields,
  initialFormData,
  onDirtyChange,
  onAutoSave,
  autoSaveEnabled = true,
  autoSaveDebounceMs = 5000
}) => {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [formFieldValues, setFormFieldValues] = useState<{ [key: string]: any }>(initialFormData || {});
  const [initialFieldValues, setInitialFieldValues] = useState<{ [key: string]: any }>(initialFormData || {});
  const [formFieldTemplates, setFormFieldTemplates] = useState<{ [key: string]: string }>({});
  const [activeFieldName, setActiveFieldName] = useState<string | null>(null);
  const activeFieldNameRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(() => {
    const savedScale = localStorage.getItem(PDF_ZOOM_STORAGE_KEY);
    if (savedScale) {
      const val = parseFloat(savedScale);
      return isNaN(val) ? 1.0 : val;
    }
    return 1.0;
  });
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isManualSaving, setIsManualSaving] = useState(false);

  const pdfDocRef = useRef<any>(null);
  const isLoadingRef = useRef(false);
  const lastPdfBytesRef = useRef<string>('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to evaluate a formula with field values
  const evaluateFormula = (formula: string, fieldValues: { [key: string]: any }): number | null => {
    try {
      let evaluatedFormula = formula;
      for (const [fieldName, fieldValue] of Object.entries(fieldValues)) {
        // Strip commas from formatted numbers before parsing
        const rawValue = String(fieldValue).replace(/,/g, '');
        const numericValue = parseFloat(rawValue) || 0;
        const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
        evaluatedFormula = evaluatedFormula.replace(regex, numericValue.toString());
      }
      // eslint-disable-next-line no-new-func
      const result = new Function(`return ${evaluatedFormula}`)();
      return typeof result === 'number' && !isNaN(result) ? result : null;
    } catch (e) {
      return null;
    }
  };

  // Process template expressions in text fields
  // Supports: {{PRICE}}, {{PRICE|2}}, {{PRICE|2|nocomma}}
  const processTemplateExpressions = (text: string, fieldValues: { [key: string]: any }): string => {
    if (!text || typeof text !== 'string') return text;

    const templateRegex = /\{\{([^}]+)\}\}/g;

    return text.replace(templateRegex, (match, expression) => {
      try {
        let precision = 2;
        let useThousandsSeparator = true;
        let formula = expression.trim();

        // Parse options: formula|precision|nocomma
        const parts = formula.split('|').map((p: string) => p.trim());
        formula = parts[0];

        if (parts.length > 1) {
          const parsedPrecision = parseInt(parts[1]);
          if (!isNaN(parsedPrecision)) {
            precision = parsedPrecision;
          }
        }

        if (parts.length > 2 && parts[2].toLowerCase() === 'nocomma') {
          useThousandsSeparator = false;
        }

        const result = evaluateFormula(formula, fieldValues);

        if (result !== null) {
          return formatNumber(result, precision, useThousandsSeparator);
        }

        return match;
      } catch (e) {
        return match;
      }
    });
  };

  // Process templates to get the actual form data (used by auto-save)
  const getProcessedFormDataForAutoSave = () => {
    const processedData = { ...formFieldValues };
    for (const fieldCode in formFieldTemplates) {
      const template = formFieldTemplates[fieldCode];
      if (template && template.includes('{{')) {
        const processed = processTemplateExpressions(template, formFieldValues);
        processedData[fieldCode] = processed;
      }
    }
    return processedData;
  };

  // Auto-save hook integration
  const {
    saveStatus: autoSaveStatus,
    error: autoSaveError,
    hasUnsavedChanges,
    forceSave,
    clearError: clearAutoSaveError,
    resetSavedData
  } = useAutoSave({
    data: getProcessedFormDataForAutoSave(),
    onSave: async (data) => {
      if (onAutoSave) {
        await onAutoSave(data);
      }
    },
    debounceMs: autoSaveDebounceMs,
    enabled: autoSaveEnabled && !!onAutoSave,
    skipSavedStatus: true // Go directly from 'saving' to 'idle', no green checkmark
  });

  // Expose forceSave to parent components via ref if needed
  const forceSaveRef = useRef(forceSave);
  forceSaveRef.current = forceSave;

  // Update hasUnsavedChanges in parent for beforeunload handling
  useEffect(() => {
    if (onDirtyChange && onAutoSave) {
      // When auto-save is enabled, use the hook's hasUnsavedChanges
      onDirtyChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onAutoSave, onDirtyChange]);

  // Reset saved data when initialFormData changes (new form loaded)
  useEffect(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      resetSavedData(initialFormData);
    }
  }, [initialFormData, resetSavedData]);

  // Save zoom level when it changes
  useEffect(() => {
    localStorage.setItem(PDF_ZOOM_STORAGE_KEY, scale.toString());
  }, [scale]);

  // Track dirty state and notify parent
  useEffect(() => {
    if (!onDirtyChange) {
      console.log('[InteractivePDFViewer] Dirty check skipped - no onDirtyChange callback');
      return;
    }

    // Smart comparison that ignores fields that were just "touched" but not actually changed
    // A field is considered changed only if:
    // 1. It has a different non-empty value than the initial value
    // 2. It was empty and now has a value, or vice versa
    const isValueEmpty = (val: any): boolean => {
      return val === undefined || val === null || val === '' ||
        (Array.isArray(val) && val.length === 0);
    };

    const normalizeValue = (val: any, key: string): any => {
      // For checkbox fields, treat empty/"false"/false as equivalent (unchecked)
      if (key.endsWith('_checkbox') || key.startsWith('checkbox_')) {
        if (isValueEmpty(val) || val === false || val === 'false') return 'unchecked';
        if (val === true || val === 'true') return 'checked';
        return String(val);
      }
      if (isValueEmpty(val)) return '';
      if (typeof val === 'boolean') return val ? 'true' : 'false';
      if (Array.isArray(val)) return JSON.stringify(val.sort());
      return String(val);
    };

    let isDirty = false;
    const changedFields: string[] = [];
    const allKeys = new Set([...Object.keys(formFieldValues), ...Object.keys(initialFieldValues)]);

    for (const key of allKeys) {
      const currentVal = normalizeValue(formFieldValues[key], key);
      const initialVal = normalizeValue(initialFieldValues[key], key);

      if (currentVal !== initialVal) {
        changedFields.push(`${key}: "${initialVal}" -> "${currentVal}"`);
        isDirty = true;
      }
    }

    if (isDirty) {
      console.log('[InteractivePDFViewer] Dirty - changed fields:', changedFields.slice(0, 5)); // Show first 5
    }
    console.log('[InteractivePDFViewer] Dirty check - isDirty:', isDirty);
    onDirtyChange(isDirty);
  }, [formFieldValues, initialFieldValues, onDirtyChange]);

  // Update both initial and current field values when initialFormData prop changes
  // This ensures parameter values from other forms are included in the baseline
  // We MERGE rather than replace to preserve PDF field defaults that were discovered
  useEffect(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      console.log('[InteractivePDFViewer] initialFormData changed, merging, keys:', Object.keys(initialFormData).length);
      setInitialFieldValues(prev => ({ ...prev, ...initialFormData }));
      setFormFieldValues(prev => ({ ...prev, ...initialFormData }));
    }
  }, [initialFormData]);

  // Load PDF when bytes change
  useEffect(() => {
    if (!pdfBytes || loading || isLoadingRef.current) {
      return;
    }

    // Check if PDF bytes actually changed
    const currentPdfBytesStr = Array.from(pdfBytes).join(',');
    if (currentPdfBytesStr === lastPdfBytesRef.current) {
      return;
    }

    const loadPDF = async () => {
      // Save scroll position before loading new PDF
      const savedScrollTop = scrollContainerRef.current?.scrollTop || 0;
      const savedScrollLeft = scrollContainerRef.current?.scrollLeft || 0;

      try {
        isLoadingRef.current = true;
        if (!isPDFJSAvailable()) {
          throw new Error('PDF.js is not available or not properly configured');
        }

        // Don't set isReady(false) - keep showing old PDF while loading new one
        setError(null);
        // Don't reset form field values here - they're managed by initialFormData useEffect
        // Don't reset templates - let them persist
        // Don't set numPages to 0 - keep showing old page count

        // Keep reference to old PDF doc
        const oldPdfDoc = pdfDocRef.current;

        const pdfData = pdfBytes.slice(0);
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

        // Now that new PDF is ready, destroy old one
        if (oldPdfDoc) {
          try {
            oldPdfDoc.destroy();
          } catch (e) {
            // Ignore errors during cleanup
          }
        }

        // Atomically swap to new PDF
        pdfDocRef.current = pdf;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setIsReady(true);
        lastPdfBytesRef.current = currentPdfBytesStr;

        // Restore scroll position after rendering completes
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = savedScrollTop;
            scrollContainerRef.current.scrollLeft = savedScrollLeft;
          }
        });
      } catch (error) {
        setError(`Failed to load PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadPDF();

    return () => {
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (e) {
          // Ignore errors during cleanup
        }
        pdfDocRef.current = null;
      }
    };
  }, [pdfBytes, loading, retryTrigger]);

  // Calculate dependent fields based on calculatedFrom configurations
  const calculateDependentFields = (
    changedFieldName: string,
    newValues: { [key: string]: any },
    currentTemplates: { [key: string]: string },
    currentActiveField: string | null
  ): { [key: string]: any } => {
    if (!form?.formSections) return newValues;

    const updatedValues = { ...newValues };
    const changedFields = new Set<string>([changedFieldName]);
    let hasChanges = true;
    const maxIterations = 10;
    let iteration = 0;

    while (hasChanges && iteration < maxIterations) {
      hasChanges = false;
      iteration++;

      for (const section of form.formSections) {
        if (!section.formFields) continue;

        for (const field of section.formFields) {
          try {
            const config = field.configurationJson ? JSON.parse(field.configurationJson) : {};
            const calculatedFrom = config?.calculatedFrom;

            if (calculatedFrom?.fields && calculatedFrom?.formula) {
              if (calculatedFrom.fields.includes(field.code)) {
                continue;
              }

              if (field.code === changedFieldName) {
                continue;
              }

              const dependsOnChangedField = calculatedFrom.fields.some((sourceField: string) =>
                changedFields.has(sourceField)
              );

              if (dependsOnChangedField) {
                const result = evaluateFormula(calculatedFrom.formula, updatedValues);

                if (result !== null) {
                  const precision = calculatedFrom.precision ?? 2;
                  // Check for numberFormat config to apply thousand separators
                  const numberFormatConfig = config?.numberFormat;
                  let formattedResult: string;
                  if (numberFormatConfig) {
                    const decimalPlaces = numberFormatConfig.decimalPlaces ?? precision;
                    const useThousandsSeparator = numberFormatConfig.useThousandsSeparator ?? true;
                    formattedResult = formatNumber(result, decimalPlaces, useThousandsSeparator);
                  } else {
                    formattedResult = result.toFixed(precision);
                  }

                  if (updatedValues[field.code] !== formattedResult) {
                    updatedValues[field.code] = formattedResult;
                    changedFields.add(field.code);
                    hasChanges = true;
                  }
                }
              }
            }
          } catch (e) {
            // Ignore errors processing individual fields
          }
        }
      }
    }

    // Process template expressions for display
    const finalValues = { ...updatedValues };

    for (const fieldCode in currentTemplates) {
      const template = currentTemplates[fieldCode];

      if (template && template.includes('{{')) {
        if (fieldCode === currentActiveField) {
          finalValues[fieldCode] = template;
        } else {
          const processedValue = processTemplateExpressions(template, finalValues);
          finalValues[fieldCode] = processedValue;
        }
      }
    }

    return finalValues;
  };

  const handleFieldFocus = (name: string) => {
    setActiveFieldName(name);
    activeFieldNameRef.current = name;
    setFormFieldTemplates(currentTemplates => {
      setFormFieldValues(prev => {
        const updated = calculateDependentFields(name, prev, currentTemplates, name);
        return updated;
      });
      return currentTemplates;
    });
  };

  const handleFieldBlur = (name: string, value: any) => {
    setActiveFieldName(null);
    activeFieldNameRef.current = null;

    // Check if field has numberFormat config and apply formatting
    let formattedValue = value;
    if (form?.formSections && value && typeof value === 'string' && !value.includes('{{')) {
      for (const section of form.formSections) {
        const field = section.formFields?.find(f => f.code === name);
        if (field) {
          try {
            const config = field.configurationJson ? JSON.parse(field.configurationJson) : {};
            if (config?.numberFormat) {
              const rawValue = String(value).replace(/,/g, '');
              const num = parseFloat(rawValue);
              if (!isNaN(num)) {
                const decimalPlaces = config.numberFormat.decimalPlaces ?? 2;
                const useThousandsSeparator = config.numberFormat.useThousandsSeparator ?? true;
                formattedValue = formatNumber(num, decimalPlaces, useThousandsSeparator);
              }
            }
          } catch (e) {
            // Ignore parsing errors
          }
          break;
        }
      }
    }

    setFormFieldTemplates(prev => {
      const newTemplates = { ...prev };

      if (value && typeof value === 'string' && value.includes('{{')) {
        if (newTemplates[name] !== value) {
          newTemplates[name] = value;
        }
      } else if (newTemplates[name]) {
        const storedTemplate = newTemplates[name];
        const processedTemplate = processTemplateExpressions(storedTemplate, { ...formFieldValues, [name]: formattedValue });

        if (formattedValue !== processedTemplate) {
          delete newTemplates[name];
        }
      }

      setFormFieldValues(prevValues => {
        const newValues = { ...prevValues, [name]: formattedValue };
        return calculateDependentFields(name, newValues, newTemplates, null);
      });

      return newTemplates;
    });
  };

  const handleFieldChange = (name: string, value: any, isFromFocus: boolean = false) => {
    setFormFieldTemplates(prev => {
      const newTemplates = { ...prev };

      if (!isFromFocus) {
        if (value && typeof value === 'string' && value.includes('{{')) {
          if (newTemplates[name] !== value) {
            newTemplates[name] = value;
          }
        } else if (newTemplates[name]) {
          const storedTemplate = newTemplates[name];
          const processedTemplate = processTemplateExpressions(storedTemplate, { ...formFieldValues, [name]: value });

          if (value !== processedTemplate) {
            delete newTemplates[name];
          }
        }
      }

      setFormFieldValues(prevValues => {
        const newValues = { ...prevValues, [name]: value };
        const currentActiveField = activeFieldNameRef.current;
        return calculateDependentFields(name, newValues, newTemplates, currentActiveField);
      });

      return newTemplates;
    });
  };

  const handleFieldsFound = (values: { [key: string]: any }) => {
    const templatesInValues: { [key: string]: string } = {};
    Object.entries(values).forEach(([k, v]) => {
      if (v && typeof v === 'string' && v.includes('{{')) {
        templatesInValues[k] = v;
      }
    });

    const hasTemplates = Object.keys(templatesInValues).length > 0;

    // Also update initialFieldValues for any newly discovered PDF fields
    // This keeps both states in sync so they don't cause false dirty detection
    setInitialFieldValues(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(values).forEach(([k, v]) => {
        if (next[k] === undefined) {
          next[k] = v;
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setFormFieldTemplates(prev => {
      const templates = { ...prev };
      let changed = false;

      Object.entries(templatesInValues).forEach(([k, v]) => {
        if (!templates[k]) {
          templates[k] = v;
          changed = true;
        }
      });

      if (hasTemplates) {
        setFormFieldValues(prevValues => {
          const next = { ...prevValues };
          let valuesChanged = false;

          Object.entries(values).forEach(([k, v]) => {
            if (next[k] === undefined) {
              next[k] = v;
              valuesChanged = true;
            }
          });

          if (valuesChanged) {
            const processed = calculateDependentFields('', next, templates, null);
            return processed;
          }

          return next;
        });
      }

      return changed ? templates : prev;
    });

    if (!hasTemplates) {
      setFormFieldValues(prev => {
        const next = { ...prev };
        let changed = false;
        Object.entries(values).forEach(([k, v]) => {
          if (next[k] === undefined) {
            next[k] = v;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));

  // Process templates in field values for saving/downloading
  const getProcessedFormData = () => {
    const processedData = { ...formFieldValues };

    for (const fieldCode in formFieldTemplates) {
      const template = formFieldTemplates[fieldCode];
      if (template && template.includes('{{')) {
        const processed = processTemplateExpressions(template, formFieldValues);
        processedData[fieldCode] = processed;
      }
    }

    return processedData;
  };

  const handleDownload = () => {
    setIsPrinting(true);
    const processedData = getProcessedFormData();
    onDownloadAndSave?.(processedData, false, false);
    setTimeout(() => setIsPrinting(false), 500);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    const processedData = getProcessedFormData();
    onDownloadAndSave?.(processedData, false, true);
    setTimeout(() => setIsPrinting(false), 500);
  };

  const handleSave = async () => {
    // If auto-save is enabled, use forceSave for immediate save
    if (onAutoSave && autoSaveEnabled) {
      await forceSave();
      return;
    }

    // Otherwise, use the manual save (which generates PDF)
    setIsManualSaving(true);
    try {
      const processedData = getProcessedFormData();
      await onDownloadAndSave?.(processedData, true, false);
    } finally {
      setIsManualSaving(false);
    }
  };

  // Get the display save status (prioritize auto-save status when enabled)
  const getDisplaySaveStatus = (): SaveStatus => {
    if (isManualSaving) return 'saving';
    if (onAutoSave && autoSaveEnabled) return autoSaveStatus;
    return 'idle';
  };

  const displaySaveStatus = getDisplaySaveStatus();
  const isSaveDisabled = displaySaveStatus === 'saving';

  // Render the save button icon based on status
  const renderSaveButtonIcon = () => {
    switch (displaySaveStatus) {
      case 'saving':
        // Show save icon with spinner overlay
        return (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SaveIcon sx={{ opacity: 0.3 }} />
            <CircularProgress
              size={28}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-14px',
                marginLeft: '-14px'
              }}
            />
          </Box>
        );
      case 'error':
        return (
          <Tooltip title={autoSaveError?.message || 'Save failed. Will retry on next change.'}>
            <ErrorIcon sx={{ color: 'error.main' }} />
          </Tooltip>
        );
      default:
        return <SaveIcon />;
    }
  };

  // Get save button color based on status
  const getSaveButtonColor = (): 'primary' | 'success' | 'error' | 'inherit' => {
    switch (displaySaveStatus) {
      case 'saved':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  // Get save button title based on status
  const getSaveButtonTitle = (): string => {
    switch (displaySaveStatus) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return 'Saved';
      case 'error':
        return autoSaveError?.message || 'Save failed';
      default:
        return hasUnsavedChanges ? 'Save Data' : 'All changes saved';
    }
  };

  const handleRetry = () => {
    if (onRetry) onRetry();
    else {
      setError(null);
      setPdfDoc(null);
      setRetryTrigger(prev => prev + 1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '600px', width: '100%' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Generating PDF...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '600px', width: '100%', p: 2 }}>
        <Typography variant="h6" color="error" gutterBottom>PDF Loading Error</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{error}</Typography>
        <Button variant="outlined" onClick={handleRetry}>Retry Loading PDF</Button>
      </Box>
    );
  }

  if (!pdfBytes) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>PDF not available</Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', p: 2 }}>
      {/* Action Buttons */}
      <Box display="flex" gap={1} justifyContent="center" sx={{ mb: 2 }}>
        <IconButton onClick={handleZoomOut}><ZoomOutIcon /></IconButton>
        <Typography variant="body2" sx={{ alignSelf: 'center', mx: 1 }}>{Math.round(scale * 100)}%</Typography>
        <IconButton onClick={handleZoomIn}><ZoomInIcon /></IconButton>
        <Box sx={{ mx: 2, height: '24px', borderLeft: '1px solid #ccc' }} />
        <IconButton onClick={handleDownload} color="primary" title="Download PDF"><DownloadIcon /></IconButton>
        <IconButton onClick={handlePrint} color="primary" title="Print PDF"><PrintIcon /></IconButton>
        <Tooltip title={getSaveButtonTitle()}>
          <span>
            <IconButton
              onClick={handleSave}
              color={getSaveButtonColor()}
              disabled={isSaveDisabled}
            >
              {renderSaveButtonIcon()}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Pages Container */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid #ccc',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            padding: '20px'
          }}>
          {isReady && pdfDoc && Array.from({ length: numPages }).map((_, i) => (
            <PDFPageComponent
              key={`${retryTrigger}-${i + 1}`}
              pdfDoc={pdfDoc}
              pageNumber={i + 1}
              scale={scale}
              currentFieldValues={formFieldValues}
              onFieldChange={handleFieldChange}
              onFieldFocus={handleFieldFocus}
              onFieldBlur={handleFieldBlur}
              onFieldsFound={handleFieldsFound}
              form={form}
              userDataFields={userDataFields}
            />
          ))}
          {!isReady && <CircularProgress />}
        </Box>
      </Box>
    </Box>
  );
};

export default InteractivePDFViewer;
